/**
 * Helper mở Outlook desktop soạn thư từ web — dùng chung cho Send-DNTT / Send-XNTT
 * (ProcurementPayDataListView) và Send-DNTT VAT (PayDataSendVatDialog).
 *
 * Cơ chế (theo Access, giữ Outlook App desktop, KHÔNG dùng OWA/Graph):
 *   • Windows classic: tải .eml (X-Unsent:1) → double-click mở compose + auto đính kèm.
 *   • Mac/khác: mailto (mở Outlook soạn thư) + tải Excel để user kéo đính kèm tay
 *     (Outlook-for-Mac bỏ qua X-Unsent nên .eml mở read-only).
 */
import type { PayDataMailDraft } from '../types/api'

// Trích filename từ Content-Disposition (BE trả .eml tên "DNTT_....eml").
export const filenameFromDisposition = (disp: string, fallback: string): string => {
  const m = /filename\s*=\s*"?([^"]+)"?/i.exec(disp)
  return m?.[1]?.trim() || fallback
}

// Body BE trả có vài tag HTML (<b>…</b>) — mailto chỉ mang text thường nên
// strip tag về plain text (nhánh mailto không đính kèm/không giữ HTML được).
export const htmlToPlainText = (html: string): string =>
  html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')

// Build URL mailto (truyền sẵn To/CC/Subject/Body). `to` giữ nguyên (email +
// dấu ',' phân tách nhiều người nhận — RFC 6068).
export const buildMailtoHref = (d: PayDataMailDraft): string => {
  const params: string[] = []
  if (d.cc) params.push(`cc=${encodeURIComponent(d.cc)}`)
  if (d.subject) params.push(`subject=${encodeURIComponent(d.subject)}`)
  if (d.body) params.push(`body=${encodeURIComponent(htmlToPlainText(d.body))}`)
  return `mailto:${d.to}${params.length ? `?${params.join('&')}` : ''}`
}

// Mở mailto bằng synthetic anchor-click (mở Outlook desktop soạn thư).
export const openOutlookDraft = (d: PayDataMailDraft): void => {
  const a = document.createElement('a')
  a.href = buildMailtoHref(d)
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

// Chỉ Outlook-for-Windows hiểu .eml X-Unsent (mở compose có nút Send + tự đính
// kèm). Mac/khác → .eml mở read-only → phải mailto + tải Excel kéo đính kèm tay.
export const isWindowsOS = (): boolean =>
  /Win/i.test(navigator.userAgent || (navigator as { platform?: string }).platform || '')

// base64 → Blob (tải file Excel ở nhánh Mac).
export const base64ToBlob = (b64: string, mime: string): Blob => {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

// Đọc `detail` từ response lỗi (fallback nếu không parse được JSON).
export const errorDetail = async (resp: Response, fallback: string): Promise<string> => {
  try {
    return String((await resp.json())?.detail ?? fallback)
  } catch {
    return fallback
  }
}

// Tải Blob về máy với tên file cho trước.
export const triggerDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

// MIME của file Excel .xlsx.
export const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
